import React, { useRef, useEffect } from "react"
import * as am5 from "@amcharts/amcharts5"
import * as am5radar from "@amcharts/amcharts5/radar"
import * as am5xy from "@amcharts/amcharts5/xy"
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated"
import * as am5plugins_exporting from "@amcharts/amcharts5/plugins/exporting"

// Utils
import { filterMenuItems, getNumberFormat, transformeData } from "../utils/global"
import { formatRadarTextLabel, transformSecondGroupByToData, getValueData } from "./RadarChartUtils"
import {
  createLegend,
  clearLegends,
  createExportMenu,
  configureNumberFormatting,
  resolveMappedColor,
} from "../utils/utils"
import type { ColorMapperRow } from "../utils/utils"

// Types
interface RadarChartProps {
  idChart?: string
  widgetTitle?: string
  enableExportMenu?: boolean
  gridType?: "circles" | "polygons"
  dataChart?: any[]
  dataNames?: string[]
  valueAxisMax?: number
  valueAxisMin?: number
  valueForceMinMax?: boolean
  valueData?: string[]
  categoryData?: string | string[]
  colorSetTheme?: string[]
  colorMapper?: ColorMapperRow[]
  enableToolTip?: boolean
  disableLabel?: boolean
  visibilityLegend?: boolean
  displayTypeLegendLabels?: "truncate" | "wrap" | "default"
  legendPosition?: "top" | "bottom" | "left" | "right"
  bulletSeries?: "circle" | "rectangle" | "label"
  bulletHeight?: number
  bulletWidth?: number
  bulletRadius?: number
  numberDecimal?: number
  decimalSeparator?: string
  showSpaceForThousand?: boolean
  hideLabelIfPercentTooSmall?: boolean
  valuePercentUnderWhichHideLabel?: number
  fillOpacityCurve?: number
  sizeBullet?: number
  colorBullet?: string
  strokeWidthLine?: number
  lineStyle?: "dashed" | "solid" | "dotted"
  width?: string
  height?: string
  showAsPercentage?: boolean
  fieldsData?: any
  requestLoad?: any
}

const RadarChart: React.FC<RadarChartProps> = ({
  idChart,
  widgetTitle = "radarChart",
  enableExportMenu = false,
  gridType = "polygons",
  dataChart = [],
  dataNames = [],
  valueAxisMax = 500,
  valueAxisMin = 0,
  valueForceMinMax = false,
  valueData = [],
  categoryData = "country",
  colorSetTheme = ["#e6f99a", "red"],
  colorMapper = [],
  enableToolTip = true,
  disableLabel = false,
  visibilityLegend = true,
  displayTypeLegendLabels = "truncate",
  legendPosition = "right",
  bulletSeries = "circle",
  bulletHeight = 5,
  bulletWidth = 5,
  bulletRadius,
  numberDecimal = 2,
  decimalSeparator = ",",
  showSpaceForThousand = true,
  hideLabelIfPercentTooSmall = false,
  valuePercentUnderWhichHideLabel = 50,
  fillOpacityCurve = 0,
  sizeBullet = 5,
  colorBullet = "",
  strokeWidthLine = 3,
  lineStyle = "solid",
  width = "100%",
  height = "100%",
  showAsPercentage = false,
  fieldsData,
  requestLoad,
}) => {
  const ID = useRef(idChart || `RadarChart${Math.random()}`).current
  const chartRef = useRef<am5radar.RadarChart | null>(null)
  const rootRef = useRef<am5.Root | null>(null)

  const dataTransformed = transformeData(requestLoad, fieldsData, dataChart)

  const initChart = () => {
    const root = am5.Root.new(ID)
    root._logo?.dispose()
    rootRef.current = root

    root.setThemes([am5themes_Animated.new(root)])

    const chart = root.container.children.push(
      am5radar.RadarChart.new(root, {
        panX: false,
        panY: false,
        wheelX: "none",
        wheelY: "none",
      })
    )

    chartRef.current = chart

    setupChart(chart, root)
    updateChart(chart, root)
  }

  const setupChart = (chart: am5radar.RadarChart, root: am5.Root) => {
    // Number formatting
    configureNumberFormatting(root, {
      decimalSeparator,
      numberDecimal,
      showSpaceForThousand,
    })

    // Export menu
    createExportMenu(root, {
      enableExportMenu: enableExportMenu ?? false,
      filePrefix: widgetTitle,
      dataSource: dataTransformed,
    })

    // Cursor
    chart.set("cursor", am5radar.RadarCursor.new(root, {}))

    // Legend
    createLegend(chart, root, {
      visibilityLegend,
      legendPosition,
      displayTypeLegendLabels,
    })
  }

  const updateChart = (chart: am5radar.RadarChart, root: am5.Root) => {
    // Clear existing axes and series
    chart.xAxes.clear()
    chart.yAxes.clear()
    chart.series.clear()

    const transformedData = transformSecondGroupByToData(valueData, categoryData, dataChart)

    // Category axis (X)
    const categoryField = Array.isArray(categoryData) ? categoryData[0] : categoryData
    const xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: categoryField,
        renderer: am5radar.AxisRendererCircular.new(root, {}),
      })
    )

    xAxis.data.setAll(transformedData)

    // Configure labels - keep them horizontal and outside
    xAxis.get("renderer").labels.template.setAll({
      visible: !disableLabel,
      textType: "adjusted",
      oversizedBehavior: "wrap",
      maxWidth: 100,
    })

    // Label adapter for formatting
    xAxis.get("renderer").labels.template.adapters.add("text", (text: string | undefined) => {
      return formatRadarTextLabel(text)
    })

    // Hide labels if percent too small
    if (hideLabelIfPercentTooSmall) {
      xAxis
        .get("renderer")
        .labels.template.adapters.add("text", (text: string | undefined, target: any) => {
          const dataItem = target.dataItem
          if (!dataItem) return text

          let isUpperThanValue = false
          const seriesDataItems = dataItem.bullets

          if (seriesDataItems) {
            seriesDataItems.forEach((bullet: any) => {
              const valueY = bullet.dataItem?.get("valueY")
              if (valueY && valueY > valuePercentUnderWhichHideLabel) {
                isUpperThanValue = true
              }
            })
          }

          return isUpperThanValue ? text : ""
        })
    }

    // Value axis (Y)
    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5radar.AxisRendererRadial.new(root, {}),
      })
    )

    // Note: gridType is not directly settable in amCharts 5
    // The grid appearance is automatic based on the RadarChart type

    if (valueForceMinMax) {
      if (valueAxisMax !== undefined) yAxis.set("max", valueAxisMax)
      if (valueAxisMin !== undefined) yAxis.set("min", valueAxisMin)
      yAxis.set("strictMinMax", true)
    }

    // Add series
    const valueToUse = getValueData(valueData, categoryData, dataChart)

    valueToUse.forEach((value, index) => {
      const serieName = dataNames.length === valueToUse.length ? dataNames[index] : value
      const paletteColor = colorSetTheme[index % colorSetTheme.length]
      // Une branche du radar EST une valeur de legende : la regle l'emporte sur la palette.
      const serieColor = resolveMappedColor(serieName, colorMapper) ?? paletteColor

      const series = chart.series.push(
        am5radar.RadarLineSeries.new(root, {
          name: serieName,
          xAxis: xAxis,
          yAxis: yAxis,
          valueYField: value,
          categoryXField: categoryField,
          stroke: am5.color(serieColor),
          fill: am5.color(serieColor),
        })
      )

      series.strokes.template.setAll({
        strokeWidth: strokeWidthLine,
      })

      series.fills.template.setAll({
        fillOpacity: fillOpacityCurve,
        visible: fillOpacityCurve > 0,
      })

      // Line style
      if (lineStyle === "dashed") {
        series.strokes.template.set("strokeDasharray", [5, 2])
      } else if (lineStyle === "dotted") {
        series.strokes.template.set("strokeDasharray", [3, 3])
      }

      // Bullets
      switch (bulletSeries) {
        case "circle":
          series.bullets.push(() => {
            const circle = am5.Circle.new(root, {
              radius: sizeBullet,
              fill: colorBullet ? am5.color(colorBullet) : series.get("fill"),
            })
            return am5.Bullet.new(root, {
              sprite: circle,
            })
          })
          break

        case "rectangle":
          series.bullets.push(() => {
            const rectangle = am5.Rectangle.new(root, {
              width: sizeBullet,
              height: sizeBullet,
              centerX: am5.p50,
              centerY: am5.p50,
              fill: colorBullet ? am5.color(colorBullet) : series.get("fill"),
            })
            return am5.Bullet.new(root, {
              sprite: rectangle,
            })
          })
          break

        case "label":
          // Circle bullet
          series.bullets.push(() => {
            const circle = am5.Circle.new(root, {
              radius: sizeBullet,
              fill: colorBullet ? am5.color(colorBullet) : series.get("fill"),
            })
            return am5.Bullet.new(root, {
              sprite: circle,
            })
          })

          // Label bullet
          series.bullets.push(() => {
            const label = am5.Label.new(root, {
              text: "{valueY}",
              centerX: am5.p50,
              centerY: am5.p50,
              populateText: true,
            })
            return am5.Bullet.new(root, {
              sprite: label,
            })
          })
          break
      }

      // Tooltip
      if (enableToolTip && dataNames[index] !== "" && dataNames[index] !== null) {
        const valueQualifier = showAsPercentage ? " %" : ""
        series.set(
          "tooltip",
          am5.Tooltip.new(root, {
            labelText: `{categoryX} : {valueY}${valueQualifier}`,
          })
        )
      }

      series.data.setAll(transformedData)
    })

    const legend = chart.children.values.find(child => child instanceof am5.Legend) as
      | am5.Legend
      | undefined
    if (legend) {
      legend.data.setAll(chart.series.values)
    }
  }

  useEffect(() => {
    initChart()

    return () => {
      if (rootRef.current) {
        rootRef.current.dispose()
      }
    }
  }, [])

  useEffect(() => {
    if (chartRef.current && rootRef.current) {
      const chart = chartRef.current
      const root = rootRef.current

      // Clear legends
      clearLegends(chart)

      // Reapply chart configurations (including recreating legend)
      setupChart(chart, root)

      // Recreate axes and series
      updateChart(chart, root)
    }
  })

  return <div id={ID} style={{ width, height }} />
}

export default RadarChart
